import {
  Bell,
  ChevronRight,
  CircleCheck,
  Clock,
  Droplets,
  FileWarning,
  Target,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { createElement, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDismissable } from '@/hooks/use-dismissable';
import { cn, formatDateTimeValue } from '@/lib/utils';

import { useNotificaciones } from '../notificaciones.api';
import type { CategoriaNotificacion, Notificacion } from '../notificaciones.types';

/** Icono por familia de aviso (la categoría la decide el backend). */
const ICONOS: Record<CategoriaNotificacion, LucideIcon> = {
  comprobantes: FileWarning,
  ric: Droplets,
  turnos: Clock,
  metas: Target,
  mantenimiento: Wrench,
};

/** Colores del icono según la severidad. */
const ESTILOS: Record<Notificacion['severidad'], string> = {
  alta: 'bg-rose-50 text-rose-600',
  media: 'bg-amber-50 text-amber-600',
  info: 'bg-brand-50 text-brand-600',
};

/**
 * Campana de notificaciones de la barra superior.
 *
 * Muestra un contador con los avisos pendientes (comprobantes rechazados o sin
 * respuesta de SUNAT, tanques sin medición de RIC, turnos abiertos, metas en
 * riesgo y tareas de mantenimiento vencidas). Cada aviso abre la pantalla que lo
 * resuelve; el backend ya filtra por los grupos del usuario y entrega el enlace.
 */
export function NotificacionesMenu() {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const { data, isError, isFetching, refetch } = useNotificaciones();
  const cerrar = useCallback(() => setAbierto(false), []);
  const contenedor = useDismissable<HTMLDivElement>(cerrar);

  const notificaciones = data?.notificaciones ?? [];
  const total = data?.total ?? 0;
  const altas = data?.porSeveridad.alta ?? 0;

  const alternar = () => {
    const siguiente = !abierto;

    setAbierto(siguiente);

    // Al abrir se refresca: el intervalo es de 5 minutos.
    if (siguiente) void refetch();
  };

  const abrirAviso = (aviso: Notificacion) => {
    setAbierto(false);

    if (aviso.enlace) navigate(aviso.enlace);
  };

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={total ? `Notificaciones: ${total} pendientes` : 'Notificaciones'}
        title="Notificaciones"
        className="relative flex size-10 cursor-pointer items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
      >
        <Bell className="size-5" aria-hidden="true" />

        {total > 0 ? (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-4 text-white',
              altas > 0 ? 'bg-rose-600' : 'bg-amber-500',
            )}
          >
            {total > 9 ? '9+' : total}
          </span>
        ) : null}
      </button>

      {abierto ? (
        <div
          role="menu"
          aria-label="Notificaciones"
          className="absolute right-0 z-30 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] animate-fade-up overflow-hidden rounded-card border border-ink-100 bg-white shadow-card"
        >
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
            <p className="text-sm font-semibold text-ink-900">Notificaciones</p>
            <span className="text-xs text-ink-400">
              {total ? `${total} pendiente${total === 1 ? '' : 's'}` : 'Sin pendientes'}
            </span>
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            {isError ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-ink-600">No se pudieron cargar los avisos.</p>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="mt-2 cursor-pointer text-xs font-semibold text-brand-600 hover:underline"
                >
                  Reintentar
                </button>
              </div>
            ) : null}

            {!isError && !notificaciones.length ? (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <CircleCheck className="size-7 text-emerald-500" aria-hidden="true" />
                <p className="text-sm font-medium text-ink-700">Todo en orden</p>
                <p className="text-xs text-ink-400">No hay avisos que requieran tu atención.</p>
              </div>
            ) : null}

            {!isError
              ? notificaciones.map((aviso) => (
                  <button
                    key={aviso.id}
                    type="button"
                    role="menuitem"
                    onClick={() => abrirAviso(aviso)}
                    className="group flex w-full cursor-pointer items-start gap-3 border-b border-ink-50 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-ink-50"
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg',
                        ESTILOS[aviso.severidad],
                      )}
                    >
                      {createElement(ICONOS[aviso.categoria] ?? Bell, {
                        className: 'size-4',
                        'aria-hidden': true,
                      })}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink-900">
                        {aviso.titulo}
                      </span>
                      {aviso.detalle ? (
                        <span className="mt-0.5 block text-xs leading-relaxed text-ink-500">
                          {aviso.detalle}
                        </span>
                      ) : null}
                      {aviso.fecha ? (
                        <span className="mt-1 block text-[11px] text-ink-400">
                          {formatDateTimeValue(aviso.fecha)}
                        </span>
                      ) : null}
                    </span>

                    <ChevronRight
                      className="mt-1 size-4 shrink-0 text-ink-300 transition-transform group-hover:translate-x-0.5 group-hover:text-ink-500"
                      aria-hidden="true"
                    />
                  </button>
                ))
              : null}
          </div>

          <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50/60 px-4 py-2">
            <span className="text-[11px] text-ink-400">
              {data?.generadoEn
                ? `Actualizado ${formatDateTimeValue(data.generadoEn)}`
                : 'Sin datos'}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="cursor-pointer text-[11px] font-semibold text-brand-600 hover:underline disabled:opacity-50"
            >
              {isFetching ? 'Actualizando…' : 'Actualizar'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
